import type { CuttingStrategy } from './CuttingStrategy';
import type { IMaterial, DebugStep } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';

interface PlacementResult {
    fitsCount: number;
    wastePercentage: number;
    isLandscape: boolean;
}

interface Rectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

// Interface for part with quantity count
interface PartWithCount extends IMaterial {
    count: number;
}

export class MaterialDivisionStrategy implements CuttingStrategy {
    name = 'Material Division';

    optimize(material: IMaterial, parts: IMaterial[]): ICutResult {
        // Sort parts by area (largest first) to optimize placement
        const sortedParts = [...parts].sort((a, b) => (b.width * b.height) - (a.width * a.height));
        const usedMaterials: IMaterial[] = [];
        const debugSteps: DebugStep[] = [];

        // Create a copy of parts with counts
        const remainingParts: PartWithCount[] = [...sortedParts].map(part => ({
            ...part,
            count: 1
        }));

        // Keep placing parts until no more parts left
        while (remainingParts.length > 0) {
            // Create a new material sheet
            const currentMaterial: IMaterial = {
                ...material,
                id: `material-${usedMaterials.length + 1}`,
                children: [] as IMaterial[],
                debugSteps: []
            };

            // Initialize free rectangles with one rectangle representing the whole sheet
            const freeRectangles: Rectangle[] = [
                { x: 0, y: 0, width: material.width, height: material.height }
            ];

            // Add debug step for new material
            debugSteps.push({
                type: 'check',
                reason: `Starting new material sheet #${usedMaterials.length + 1}`,
                freeRectangles: [...freeRectangles]
            });

            if (currentMaterial.debugSteps) {
                currentMaterial.debugSteps.push({
                    type: 'check',
                    reason: `Starting new material sheet #${usedMaterials.length + 1}`,
                    freeRectangles: [...freeRectangles]
                });
            }

            // Continue filling current material while there's free space and parts
            let placedAny = false;
            let placementIteration = 0;

            while (freeRectangles.length > 0 && remainingParts.length > 0) {
                placementIteration++;

                // Find the best free rectangle to use
                const freeRect = this.findBestFreeRectangle(freeRectangles);

                // Find the best part and orientation for the current free rectangle
                const bestPlacement = this.findBestPartPlacement(
                    remainingParts,
                    freeRect.width,
                    freeRect.height
                );

                // If no suitable placement was found, remove this rectangle and try another
                if (!bestPlacement) {
                    // Add debug step for rejected rectangle
                    debugSteps.push({
                        type: 'reject',
                        reason: `No suitable part found for free rectangle at (${freeRect.x},${freeRect.y}) size ${freeRect.width}x${freeRect.height}`,
                        freeRectangles: [...freeRectangles]
                    });

                    if (currentMaterial.debugSteps) {
                        currentMaterial.debugSteps.push({
                            type: 'reject',
                            reason: `No suitable part found for free rectangle at (${freeRect.x},${freeRect.y}) size ${freeRect.width}x${freeRect.height}`,
                            freeRectangles: [...freeRectangles]
                        });
                    }

                    // Remove this free rectangle
                    this.removeFreeRectangle(freeRectangles, freeRect);
                    continue;
                }

                const { part, isLandscape, fitsCount } = bestPlacement;
                placedAny = true;

                // Calculate dimensions based on orientation
                const partWidth = isLandscape ? part.width : part.height;
                const partHeight = isLandscape ? part.height : part.width;

                // Calculate how many parts we can actually place in a row
                const actualFitsCount = Math.min(
                    Math.floor(freeRect.width / partWidth),
                    fitsCount,
                    part.count
                );

                // Place parts horizontally
                let currentX = freeRect.x;
                const currentY = freeRect.y;
                const placedParts: IMaterial[] = [];

                for (let i = 0; i < actualFitsCount; i++) {
                    // Create a placed part
                    const placedPart: IMaterial = {
                        ...part,
                        x: currentX,
                        y: currentY,
                        width: partWidth,
                        height: partHeight,
                        rotated: !isLandscape // rotated if placed in portrait
                    };

                    // Add debug step for part placement
                    debugSteps.push({
                        type: 'place',
                        part: placedPart,
                        position: { x: currentX, y: currentY },
                        reason: `Placing part ${part.id} (${isLandscape ? 'landscape' : 'portrait'}) at (${currentX},${currentY})`
                    });

                    if (currentMaterial.debugSteps) {
                        currentMaterial.debugSteps.push({
                            type: 'place',
                            part: placedPart,
                            position: { x: currentX, y: currentY },
                            reason: `Placing part ${part.id} (${isLandscape ? 'landscape' : 'portrait'}) at (${currentX},${currentY})`
                        });
                    }

                    // Add to current material
                    currentMaterial.children!.push(placedPart);
                    placedParts.push(placedPart);

                    // Move to next position
                    currentX += partWidth;
                }

                // Update free rectangles after placement
                this.updateFreeRectangles(
                    freeRectangles,
                    freeRect,
                    actualFitsCount * partWidth,
                    partHeight
                );

                // Add debug step for updated free rectangles
                debugSteps.push({
                    type: 'freeRectangles',
                    reason: `Updated free rectangles after placing ${actualFitsCount} parts`,
                    freeRectangles: [...freeRectangles],
                    placedParts: [...placedParts]
                });

                if (currentMaterial.debugSteps) {
                    currentMaterial.debugSteps.push({
                        type: 'freeRectangles',
                        reason: `Updated free rectangles after placing ${actualFitsCount} parts`,
                        freeRectangles: [...freeRectangles],
                        placedParts: [...placedParts]
                    });
                }

                // Find the part in the remaining parts array and update it
                for (let i = 0; i < remainingParts.length; i++) {
                    if (remainingParts[i].id === part.id) {
                        if (remainingParts[i].count <= actualFitsCount) {
                            debugSteps.push({
                                type: 'check',
                                reason: `All parts of type ${part.id} have been placed`
                            });

                            if (currentMaterial.debugSteps) {
                                currentMaterial.debugSteps.push({
                                    type: 'check',
                                    reason: `All parts of type ${part.id} have been placed`
                                });
                            }

                            remainingParts.splice(i, 1);
                        } else {
                            remainingParts[i].count -= actualFitsCount;

                            debugSteps.push({
                                type: 'check',
                                reason: `${remainingParts[i].count} parts of type ${part.id} remaining`
                            });

                            if (currentMaterial.debugSteps) {
                                currentMaterial.debugSteps.push({
                                    type: 'check',
                                    reason: `${remainingParts[i].count} parts of type ${part.id} remaining`
                                });
                            }
                        }
                        break;
                    }
                }
            }

            // If we placed at least one part on this material
            if (placedAny) {
                usedMaterials.push(currentMaterial);

                debugSteps.push({
                    type: 'check',
                    reason: `Completed material sheet #${usedMaterials.length}`,
                    placedParts: currentMaterial.children
                });
            } else {
                // If we couldn't place any parts, break to avoid infinite loop
                debugSteps.push({
                    type: 'check',
                    reason: 'Could not place any more parts on a new sheet, stopping'
                });
                break;
            }
        }

        return {
            usedMaterials,
            wastePercentage: this.calculateWaste(material, usedMaterials),
            strategyName: this.name,
            debugSteps
        };
    }

    private findBestFreeRectangle(freeRectangles: Rectangle[]): Rectangle {
        // Select the largest free rectangle by area
        // This helps maximize the use of large open spaces first
        let bestRect = freeRectangles[0];
        let bestArea = bestRect.width * bestRect.height;

        for (let i = 1; i < freeRectangles.length; i++) {
            const rect = freeRectangles[i];
            const area = rect.width * rect.height;

            if (area > bestArea) {
                bestRect = rect;
                bestArea = area;
            }
        }

        return bestRect;
    }

    private removeFreeRectangle(freeRectangles: Rectangle[], rect: Rectangle): void {
        const index = freeRectangles.findIndex(r =>
            r.x === rect.x && r.y === rect.y &&
            r.width === rect.width && r.height === rect.height
        );

        if (index !== -1) {
            freeRectangles.splice(index, 1);
        }
    }

    private updateFreeRectangles(
        freeRectangles: Rectangle[],
        usedRect: Rectangle,
        usedWidth: number,
        usedHeight: number
    ): void {
        // Remove the rectangle we just used
        this.removeFreeRectangle(freeRectangles, usedRect);

        // Create up to two new rectangles based on the remaining space

        // Right rectangle (if there's space to the right of the placed parts)
        if (usedRect.x + usedWidth < usedRect.x + usedRect.width) {
            freeRectangles.push({
                x: usedRect.x + usedWidth,
                y: usedRect.y,
                width: usedRect.width - usedWidth,
                height: usedRect.height
            });
        }

        // Bottom rectangle (if there's space below the placed parts)
        if (usedRect.y + usedHeight < usedRect.y + usedRect.height) {
            freeRectangles.push({
                x: usedRect.x,
                y: usedRect.y + usedHeight,
                width: usedWidth,  // Only as wide as the parts we just placed
                height: usedRect.height - usedHeight
            });
        }

        // Merge rectangles if possible and remove redundant ones
        this.optimizeFreeRectangles(freeRectangles);
    }

    private optimizeFreeRectangles(freeRectangles: Rectangle[]): void {
        // Remove rectangles contained within others
        for (let i = freeRectangles.length - 1; i >= 0; i--) {
            for (let j = 0; j < freeRectangles.length; j++) {
                if (i !== j && this.isContainedIn(freeRectangles[i], freeRectangles[j])) {
                    freeRectangles.splice(i, 1);
                    break;
                }
            }
        }
    }

    private isContainedIn(a: Rectangle, b: Rectangle): boolean {
        return (
            a.x >= b.x &&
            a.y >= b.y &&
            a.x + a.width <= b.x + b.width &&
            a.y + a.height <= b.y + b.height
        );
    }

    private findBestPartPlacement(
        parts: PartWithCount[],
        rectWidth: number,
        rectHeight: number
    ): { part: PartWithCount, isLandscape: boolean, fitsCount: number } | null {
        let bestWaste = 100; // 100%
        let bestPart: PartWithCount | null = null;
        let bestIsLandscape = false;
        let bestFitsCount = 0;

        // Try each part in both orientations
        for (const part of parts) {
            // Try landscape orientation (original dimensions)
            const landscapeResult = this.tryPlacement(part, rectWidth, rectHeight, true);

            // Try portrait orientation (swapped dimensions)
            const portraitResult = this.tryPlacement(part, rectWidth, rectHeight, false);

            // Choose the better option between landscape and portrait
            let bestOrientation = landscapeResult;
            let isLandscape = true;

            if (portraitResult.wastePercentage < landscapeResult.wastePercentage) {
                bestOrientation = portraitResult;
                isLandscape = false;
            } else if (portraitResult.wastePercentage === landscapeResult.wastePercentage &&
                portraitResult.fitsCount > landscapeResult.fitsCount) {
                // If waste is equal, choose the orientation that fits more parts
                bestOrientation = portraitResult;
                isLandscape = false;
            }

            // Update best placement if this is better than previous best
            if (bestOrientation.fitsCount > 0 &&
                (bestOrientation.wastePercentage < bestWaste ||
                    (bestOrientation.wastePercentage === bestWaste && bestOrientation.fitsCount > bestFitsCount))) {
                bestWaste = bestOrientation.wastePercentage;
                bestPart = part;
                bestIsLandscape = isLandscape;
                bestFitsCount = Math.min(bestOrientation.fitsCount, part.count);
            }
        }

        if (bestPart) {
            return {
                part: bestPart,
                isLandscape: bestIsLandscape,
                fitsCount: bestFitsCount
            };
        }

        return null;
    }

    private tryPlacement(
        part: IMaterial,
        rectWidth: number,
        rectHeight: number,
        isLandscape: boolean
    ): PlacementResult {
        // Calculate dimensions based on orientation
        const partWidth = isLandscape ? part.width : part.height;
        const partHeight = isLandscape ? part.height : part.width;

        // Check if part fits in both dimensions
        if (partWidth > rectWidth || partHeight > rectHeight) {
            return { fitsCount: 0, wastePercentage: 100, isLandscape };
        }

        // Calculate how many parts fit horizontally in a row
        const fitsCount = Math.floor(rectWidth / partWidth);

        if (fitsCount === 0) {
            return { fitsCount: 0, wastePercentage: 100, isLandscape };
        }

        // Calculate waste percentage for this placement
        const usedWidth = fitsCount * partWidth;
        const wasteWidth = rectWidth - usedWidth;

        // Calculate as percentage of the area used for this row
        const rowArea = rectWidth * partHeight;
        const wasteArea = wasteWidth * partHeight;
        const wastePercentage = (wasteArea / rowArea) * 100;

        return { fitsCount, wastePercentage, isLandscape };
    }

    private calculateWaste(originalMaterial: IMaterial, usedMaterials: IMaterial[]): number {
        const totalArea = originalMaterial.width * originalMaterial.height * usedMaterials.length;
        let usedArea = 0;

        usedMaterials.forEach(material => {
            if (material.children) {
                material.children.forEach(part => {
                    usedArea += part.width * part.height;
                });
            }
        });

        return ((totalArea - usedArea) / totalArea) * 100;
    }
}
